import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  SxProps,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { IPriceItinerary } from "../interfaces.lowPrice";
import { fadeIn, getCellStyles, sxStyles } from "../sharedStyles";
import {
  CustomAnimation,
  ICustomAnimationProps,
  useConfig,
} from "@thekilroygroup/shared-ui";
import { initialPriceItinerary } from "../buildLowPriceData";
import { useCountUp } from "use-count-up";
import { isBefore } from "date-fns";
import { DesktopSkeleton, MobileSkeleton } from "./Skeleton";
import { MobileButtonInner } from "./MobileButtonInner";
import { DesktopButtonInner } from "./DesktopButtonInner";

interface IProps {
  priceItinerary: IPriceItinerary;
  isSearchedDepartureDate: boolean;
  isSearchedReturnDate: boolean;
  selectedItineraryDates: { departureDate: string; returnDate: string } | null;
  height: number;
  width: number;
  selectedItineraryId: string | null;
  maxMin: { max: number; min: number };
  setSelectedItinerary: (id: string) => void;
  showYouthStudentItineraries: boolean;
}

export const CalendarButton = (props: IProps) => {
  const {
    priceItinerary,
    selectedItineraryDates,
    height,
    width,
    selectedItineraryId,
    maxMin,
    setSelectedItinerary = () => {},
    showYouthStudentItineraries,
  } = props;

  const { locale } = useConfig();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [itin, setItin] = useState<IPriceItinerary>(initialPriceItinerary);
  const [showYouthStudent, setShowYouthStudent] = useState(false);

  useEffect(() => {
    if (priceItinerary.hasStudentOrYouth) {
      if (
        showYouthStudentItineraries &&
        selectedItineraryId === priceItinerary.id &&
        priceItinerary.studentOrYouthData.price < priceItinerary.price
      ) {
        setSelectedItinerary(priceItinerary.studentOrYouthData.id);
      } else if (
        !showYouthStudentItineraries &&
        selectedItineraryId === priceItinerary.studentOrYouthData.id
      ) {
        setSelectedItinerary(priceItinerary.id);
      }
    }
  }, [showYouthStudentItineraries]);

  useEffect(() => {
    if (
      showYouthStudentItineraries &&
      priceItinerary.hasStudentOrYouth &&
      priceItinerary.studentOrYouthData.price < priceItinerary.price
    ) {
      const nextPriceItinerary: IPriceItinerary = {
        index: priceItinerary.studentOrYouthData.index,
        id: priceItinerary.studentOrYouthData.id,
        departureDate: priceItinerary.departureDate,
        returnDate: priceItinerary.returnDate,
        departureDuration: priceItinerary.studentOrYouthData.departureDuration,
        returnDuration: priceItinerary.studentOrYouthData.departureDuration,
        departureStops: priceItinerary.studentOrYouthData.departureStops,
        returnStops: priceItinerary.studentOrYouthData.returnStops,
        departureDateDifference: priceItinerary.departureDateDifference,
        returnDateDifference: priceItinerary.returnDateDifference,
        price: priceItinerary.studentOrYouthData.price,
        currency: priceItinerary.currency,
        studentOrYouthData: {
          travelerType: priceItinerary.studentOrYouthData.travelerType,
          price: priceItinerary.studentOrYouthData.price,
          id: priceItinerary.studentOrYouthData.id,
          index: priceItinerary.studentOrYouthData.index,
          departureDuration:
            priceItinerary.studentOrYouthData.departureDuration,
          returnDuration: priceItinerary.studentOrYouthData.departureDuration,
          departureStops: priceItinerary.studentOrYouthData.departureStops,
          returnStops: priceItinerary.studentOrYouthData.returnStops,
        },
        isFetching: false,
        hasStudentOrYouth: true,
      };
      setItin(nextPriceItinerary);
      setShowYouthStudent(true);
      setPrice(nextPriceItinerary.price);
      resetPrice();
    } else {
      setItin(priceItinerary);
      setShowYouthStudent(false);
      setPrice(priceItinerary.price);
      resetPrice();
    }
  }, [priceItinerary, showYouthStudentItineraries]);

  const enter = 750;
  const animation: ICustomAnimationProps = {
    type: "fade",
    timeout: { enter: enter, exit: 0 },
    delay: 250 * Math.random(),
    easing: theme.transitions.easing.easeOut,
  };

  const { desktopButton } = sxStyles;

  const [localPrice, setLocalPrice] = useState(0);

  const [price, setPrice] = useState(0);

  const { value: animatedPrice, reset: resetPrice } = useCountUp({
    isCounting: true,
    start: localPrice,
    end: price,
    duration: 0.4,
    onComplete: () => {
      setLocalPrice(price);
    },
  });

  const getIsSelectedDepartureAndReturnBefore = () => {
    if (!selectedItineraryDates) return false;
    else if (
      isBefore(
        new Date(itin.returnDate),
        new Date(selectedItineraryDates.returnDate),
      ) &&
      itin.departureDate === selectedItineraryDates.departureDate
    ) {
      return true;
    }
    return false;
  };

  const getIsSelectedReturnAndDepartureAfter = () => {
    if (!selectedItineraryDates) return false;
    else if (
      isBefore(
        new Date(selectedItineraryDates.departureDate),
        new Date(itin.departureDate),
      ) &&
      itin.returnDate === selectedItineraryDates.returnDate
    ) {
      return true;
    }
    return false;
  };

  const styles: SxProps = getCellStyles(
    selectedItineraryId === itin.id,
    width,
    getIsSelectedDepartureAndReturnBefore(),
    getIsSelectedReturnAndDepartureAfter(),
  );

  return (
    <Button
      sx={{
        transition: `background ${enter}ms`,
        animation: `${fadeIn} ${enter}ms ${theme.transitions.easing.easeOut}`,
        height: `${height}%`,
        left: `${(itin.departureDateDifference + 2) * width}%`,
        top: `${(itin.returnDateDifference + 2) * height}%`,
        ...styles,
        padding: "8px 12px 16px 10px",
        [theme.breakpoints.down("md")]: { p: 1, display: "flex" },
      }}
      disabled={itin.isFetching || itin.price === -1}
      disableElevation
      variant={selectedItineraryId === itin.id ? "contained" : "text"}
      onClick={() => setSelectedItinerary(itin.id)}
    >
      {isMobile ? (
        <>
          {itin.isFetching ? (
            <MobileSkeleton
              priceItinerary={itin}
              maxMin={maxMin}
              selectedItineraryId={selectedItineraryId}
            />
          ) : itin.price !== -1 ? (
            <CustomAnimation {...animation} style={{ width: "100%" }}>
              <MobileButtonInner
                animatedPrice={animatedPrice}
                locale={locale}
                maxMin={maxMin}
                priceItinerary={priceItinerary}
                selectedItineraryId={selectedItineraryId}
                showYouthStudent={showYouthStudent}
              />
            </CustomAnimation>
          ) : (
            "-"
          )}
        </>
      ) : (
        <Box sx={desktopButton}>
          {itin.price === -1 && !itin.isFetching && (
            <CustomAnimation {...animation} sx={{ width: "100%" }}>
              <Typography variant="h5">-</Typography>
            </CustomAnimation>
          )}

          {itin.isFetching && (
            <CustomAnimation {...animation} sx={{ width: "100%" }}>
              <DesktopSkeleton
                priceItinerary={itin}
                maxMin={maxMin}
                selectedItineraryId={selectedItineraryId}
              />
            </CustomAnimation>
          )}

          {!itin.isFetching && itin.price !== -1 && (
            <CustomAnimation {...animation} sx={{ width: "100%" }}>
              <DesktopButtonInner
                animatedPrice={animatedPrice}
                maxMin={maxMin}
                priceItinerary={priceItinerary}
                selectedItineraryId={selectedItineraryId}
                showYouthStudent={showYouthStudent}
              />
            </CustomAnimation>
          )}
        </Box>
      )}
    </Button>
  );
};
